import { useState } from "react";
import { CheckIcon } from "lucide-react";
import { availablePlans, planRegistry, type Plan, type PlanId } from "@/data/meal-plan";
import { useAuth } from "@/context/auth-context";
import { useWeekPlan } from "@/context/week-plan-context";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ProgressDots } from "@/components/ui/progress-dots";
import { focusRing } from "@/lib/format";
import { cn } from "@/lib/utils";

export function PlanPicker() {
  const { plan } = useAuth();
  const { switchPlan } = useWeekPlan();
  /** Plan the confirmation dialog asks about (kept while it animates closed). */
  const [confirmPlan, setConfirmPlan] = useState<PlanId | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** Plan being switched to (saving, switching, reloading the week). */
  const [pendingPlan, setPendingPlan] = useState<PlanId | null>(null);
  const [error, setError] = useState<string | null>(null);

  function choose(planId: PlanId) {
    if (planId === plan?.id || pendingPlan) return;
    setConfirmPlan(planId);
    setConfirmOpen(true);
  }

  async function confirmSwitch() {
    const planId = confirmPlan;
    setConfirmOpen(false);
    if (!planId) return;
    setError(null);
    setPendingPlan(planId);
    try {
      // Only my own weeks' provider offers it (this page is never in friend mode).
      if (!switchPlan) throw new Error("No puedes cambiar el plan de otra persona.");
      await switchPlan(planId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cambiar tu plan.");
    } finally {
      setPendingPlan(null);
    }
  }

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Plan nutricional</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          El cambio aplica a los días desde hoy que aún no has planificado. Los días ya
          planificados mantienen su plan.
        </p>
      </div>
      <div role="radiogroup" aria-label="Plan nutricional" className="space-y-2">
        {availablePlans.map((p) => (
          <PlanOption
            key={p.id}
            plan={p}
            selected={plan?.id === p.id}
            pending={pendingPlan === p.id}
            disabled={pendingPlan !== null}
            onSelect={() => choose(p.id)}
          />
        ))}
      </div>
      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={confirmPlan ? `¿Cambiar a ${planRegistry[confirmPlan].name}?` : ""}
        description="Los días desde hoy que aún no has planificado pasan al nuevo plan. Los días ya planificados y los días pasados no cambian. Puedes volver a tu plan anterior cuando quieras."
        confirmText="Cambiar plan"
        onConfirm={() => void confirmSwitch()}
      />
    </div>
  );
}

function PlanOption({
  plan,
  selected,
  pending,
  disabled,
  onSelect,
}: {
  plan: Plan;
  selected: boolean;
  pending: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      disabled={disabled && !pending}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
        selected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
        disabled && !pending && "opacity-60",
        focusRing,
      )}
    >
      <span
        className={cn(
          "grid size-5 shrink-0 place-items-center rounded-full border",
          selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
        )}
        aria-hidden
      >
        {selected && <CheckIcon className="size-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{plan.name}</span>
        <span className="mt-0.5 block break-words text-xs text-muted-foreground">
          {plan.regimens.map((r) => r.name).join(" · ")}
        </span>
      </span>
      {pending && <ProgressDots aria-label="Guardando" />}
    </button>
  );
}
